/**
 * Lazily resolves a dependency to prevent circular dependency issues.
 *
 * When multiple stores depend on each other, direct imports can lead to circular dependencies,
 * causing errors or undefined behavior during module initialization. This method defers the
 * resolution of a dependency until its properties are accessed, ensuring that all stores are
 * fully initialized before being used.
 *
 * Example: When store A depends on store B, and store B depends on store A, a circular
 * dependency is created. To resolve this, store A can be created lazily in store B, and store B
 * can be created lazily in store A.
 *
 * @param {() => T} getter - A function that returns the dependency to resolve lazily.
 * @returns {T} A proxy object that resolves and caches the dependency upon first access.
 */
export const lazyDependency = <T extends object>(getter: () => T): T => {
  let cachedStore: T | undefined;

  return new Proxy({} as T, {
    get(_, prop) {
      if (!cachedStore) {
        cachedStore = getter();
      }
      return (cachedStore as any)[prop];
    },
  });
};
